import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import Button from 'components/Button/Button';
import GoodsSupplierConnectionFields from 'components/GoodsSupplierConnectionFields/GoodsSupplierConnectionFields';
import Backdrop from 'components/Modal/Backdrop';
import {
  CONNECTION_ACTION,
  CONNECTION_VC_LOGIC_FORM_FIELDS,
  CONNECTION_VC_MANUFACTURE_FORM_FIELDS,
  CONNECTION_VC_SERVICE_FORM_FIELDS,
  CONNECTION_VC_SUPPLIER_FORM_FIELDS, JOINT_VENTURE, TYPE_CONNECTION_FORM,
  TYPE_CONNECTION_FORM_OPTIONS,
} from 'constants/dataSets';
import { CONNECTION_TYPE } from 'constants/formFields';
import { BUTTON_VARIANT } from 'constants/interfaces';

import LogicalAccordion from './components/LogicalAccordion';
import LogicalForm from './components/LogicalForm';
import ManufactureForm from './components/ManufactureForm';
import ServiceForm from './components/ServiceForm';
import useConnectionVCModal from './hooks/useConnectionVCModal';

import type { TConnectionVCSchema } from 'utils/validators';
import type IConnectionVCModal from './interfaces/IConnectionVCModal';
import type IExpandedState from './interfaces/IExpandedState';

import styles from './scss/ConnectionVCModal.module.scss';

function ConnectionVCModal({
  isOpen, onClose, enumData, onAddServiceConnection, selectedEdge,
}: IConnectionVCModal) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState<IExpandedState>(TYPE_CONNECTION_FORM);
  // The expansion state as of the previous run of the reset effect below, so it can tell a
  // section the user collapsed from one that was never open. Null between openings.
  const previousExpanded = useRef<IExpandedState | null>(null);

  const {
    control,
    watch,
    onResetSelectedFields,
    handleSubmit,
    reset,
  } = useConnectionVCModal({ selectedEdge, enumData });

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    document.body.style.overflow = isOpen ? 'hidden' : 'unset';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;

    const currentExpanded: IExpandedState = {
      logical: false,
      supplier: false,
      service: false,
      manufacture: false,
    };
    const currentConnectionType = selectedEdge.map((edge) => edge?.data?.connectionType);
    if (currentConnectionType) {
      currentConnectionType.forEach((type) => {
        if (type) {
          // The edge's type is `supplierOfGoods`, which the accordion keys as `supplier`.
          if (typeof type === 'string' && type.includes(TYPE_CONNECTION_FORM_OPTIONS.supplier)) {
            currentExpanded.supplier = true;
          } else {
            currentExpanded[type as keyof IExpandedState] = true;
          }
        }
      });
    }
    setIsExpanded(currentExpanded);
  }, [selectedEdge, isOpen]);

  const toggleAccordion = (type: keyof typeof TYPE_CONNECTION_FORM_OPTIONS) => {
    setIsExpanded((prev: IExpandedState) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  const onSubmitSaveConnection = handleSubmit((values) => {
    const action = selectedEdge.length > 0 ? CONNECTION_ACTION.UPDATE : CONNECTION_ACTION.CREATE;
    onAddServiceConnection(values, isExpanded, action);
    reset();
    onClose();
  });

  const onCloseModal = () => {
    onClose();
    reset();
  };

  // Collapsing a section means "remove this connection", so its fields are cleared. Only an
  // actual collapse may clear them: the sections start collapsed and are expanded from the
  // selected edge in an effect, so reacting to `false` alone would wipe the values just loaded
  // for the edge being edited before the user ever sees them.
  useEffect(() => {
    if (!isOpen) {
      previousExpanded.current = null;
      return;
    }

    const previous = previousExpanded.current;
    previousExpanded.current = isExpanded;

    if (!previous) {
      return;
    }

    if (previous.logical && !isExpanded.logical) {
      onResetSelectedFields(CONNECTION_VC_LOGIC_FORM_FIELDS as (keyof TConnectionVCSchema)[]);
    }
    if (previous.supplier && !isExpanded.supplier) {
      onResetSelectedFields(CONNECTION_VC_SUPPLIER_FORM_FIELDS as (keyof TConnectionVCSchema)[]);
    }
    if (previous.service && !isExpanded.service) {
      onResetSelectedFields(CONNECTION_VC_SERVICE_FORM_FIELDS as (keyof TConnectionVCSchema)[]);
    }
    if (isExpanded.manufacture === false) {
      onResetSelectedFields(CONNECTION_VC_MANUFACTURE_FORM_FIELDS as (keyof TConnectionVCSchema)[]);
    }
  }, [isOpen, isExpanded, onResetSelectedFields]);

  const isSaveButtonDisabled = useMemo(() => {
    if (selectedEdge.length > 0) {
      return false;
    }
    return !isExpanded.logical
      && !isExpanded.supplier
      && !isExpanded.service
      && !isExpanded.manufacture;
  }, [
    selectedEdge.length,
    isExpanded.logical,
    isExpanded.supplier,
    isExpanded.service,
    isExpanded.manufacture,
  ]);

  return isOpen ? createPortal(
    <Backdrop>
      <div className={styles.modalOverlay}>
        <div
          className={styles.modalContent}
        >
          <h2 id="modal-title" className={styles.modalTitle}>
            {t('valueChainMap.connection')}
          </h2>
          <div className={styles.modalBody}>
            <LogicalAccordion
              title={t('valueChainMap.logical')}
              isExpanded={isExpanded.logical}
              type={TYPE_CONNECTION_FORM_OPTIONS.logical}
              toggleAccordion={toggleAccordion}
            >
              <LogicalForm
                control={control}
                showField={watch(CONNECTION_TYPE)?.label === JOINT_VENTURE}
              />
            </LogicalAccordion>
            <LogicalAccordion
              title={t('valueChainMap.supplierOfGoods')}
              isExpanded={isExpanded.supplier}
              type={TYPE_CONNECTION_FORM_OPTIONS.supplier}
              toggleAccordion={toggleAccordion}
            >
              {/* Shared with the connections list on a record modal: same fields, but here they
                  sit at the form root rather than inside an array item. */}
              <GoodsSupplierConnectionFields
                control={control}
                enumData={enumData}
              />
            </LogicalAccordion>
            <LogicalAccordion
              title={t('valueChainMap.service')}
              isExpanded={isExpanded.service}
              type={TYPE_CONNECTION_FORM_OPTIONS.service}
              toggleAccordion={toggleAccordion}
            >
              <ServiceForm
                control={control}
                enumData={enumData}
              />
            </LogicalAccordion>
            <LogicalAccordion
              title={t('valueChainMap.manufacture')}
              isExpanded={isExpanded.manufacture}
              type={TYPE_CONNECTION_FORM_OPTIONS.manufacture}
              toggleAccordion={toggleAccordion}
            >
              <ManufactureForm control={control} />
            </LogicalAccordion>
          </div>
          <div className={styles.modalFooter}>
            <Button
              title={t('common.cancel')}
              variant={BUTTON_VARIANT.cancel}
              onClick={onCloseModal}
            />
            <Button
              title={t('common.save')}
              variant={BUTTON_VARIANT.primary}
              onClick={onSubmitSaveConnection}
              disabled={isSaveButtonDisabled}
            />
          </div>
        </div>
      </div>
    </Backdrop>,
    document.body,
  ) : null;
}

export default ConnectionVCModal;
